// contexts/InstagramContext.tsx
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getInstagramMedia } from '../services/api';

interface InstagramMedia {
  id: string;
  type: 'image' | 'video' | 'carousel';
  media_url: string;
  permalink: string;
  caption?: string;
  timestamp: string;
  formatted_date?: string;
  url?: string;
}

interface InstagramAccount {
  username: string;
  permalink: string;
  media_count: number;
}

interface InstagramContextType {
  media: InstagramMedia[];
  account: InstagramAccount | null;
  loading: boolean;
  error: string | null;
  pagination: {
    hasMore: boolean;
    loadingMore: boolean;
  };
  loadMore: () => Promise<void>;
  refresh: () => void;
}

const InstagramContext = createContext<InstagramContextType>({
  media: [],
  account: null,
  loading: true,
  error: null,
  pagination: {
    hasMore: false,
    loadingMore: false,
  },
  loadMore: async () => {},
  refresh: () => {},
});

export const InstagramProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [media, setMedia] = useState<InstagramMedia[]>([]);
  const [account, setAccount] = useState<InstagramAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    hasMore: false,
    cursor: null as string | null,
    loadingMore: false,
  });
  const [refreshCount, setRefreshCount] = useState(0);

  const convertDataToArray = (data: any): InstagramMedia[] => {
    if (Array.isArray(data)) return data;
    return Object.values(data);
  };

  const fetchInstagramData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getInstagramMedia();
      const data = response.data;

      if (data.success) {
        const mediaArray = convertDataToArray(data.data);
        setMedia(mediaArray);

        if (data.account) {
          setAccount({
            username: data.account.username,
            permalink: `https://instagram.com/${data.account.username}`,
            media_count: data.account.media_count || mediaArray.length,
          });
        }

        setPagination({
          hasMore: !!data.pagination?.next,
          cursor: data.pagination?.cursors?.after || null,
          loadingMore: false,
        });
      } else {
        throw new Error('Failed to fetch Instagram data');
      }
    } catch (err) {
      setError('Failed to load Instagram posts');
      console.error('Instagram fetch error:', err);
      setMedia([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!pagination.hasMore || pagination.loadingMore) return;

    try {
      setPagination(prev => ({ ...prev, loadingMore: true }));
      const response = await getInstagramMedia({ after: pagination.cursor });
      const data = response.data;

      if (data.success) {
        const newMedia = convertDataToArray(data.data);
        setMedia(prev => [...prev, ...newMedia]);

        setPagination({
          hasMore: !!data.pagination?.next,
          cursor: data.pagination?.cursors?.after || null,
          loadingMore: false,
        });

        if (data.account && !account) {
          setAccount({
            username: data.account.username,
            permalink: `https://instagram.com/${data.account.username}`,
            media_count: data.account.media_count || media.length + newMedia.length,
          });
        }
      }
    } catch (err) {
      console.error('Error loading more posts:', err);
      setError('Failed to load more posts');
    } finally {
      setPagination(prev => ({ ...prev, loadingMore: false }));
    }
  }, [pagination.hasMore, pagination.cursor, pagination.loadingMore, account]);

  const refresh = useCallback(() => {
    setRefreshCount(prev => prev + 1);
  }, []);

  useEffect(() => {
    fetchInstagramData();
  }, [fetchInstagramData, refreshCount]);

  return (
    <InstagramContext.Provider
      value={{
        media,
        account,
        loading,
        error,
        pagination: {
          hasMore: pagination.hasMore,
          loadingMore: pagination.loadingMore,
        },
        loadMore,
        refresh,
      }}
    >
      {children}
    </InstagramContext.Provider>
  );
};

export const useInstagram = () => useContext(InstagramContext);